import { useEffect, useState } from "react";
import { Box, Chip, Divider, Paper, Typography } from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import axios from "axios";
import { toast } from "sonner";
import Oraganization from "./Oraganization";
import CustomLoader from "../static/CustomLoader";
import {
  MasterButton,
  MasterDialog,
  MasterConfirmDialog,
} from "../static/Masterpage";

const NAVY = "#123B5D";
const NAVY_DARK = "#0E304B";
const BORDER = "#DCE5EE";
const MUTED = "#64748B";
const BACKGROUND = "#F5F8FC";
const BLUE = "#2F6FB3";

const OraganizationListing = () => {
  const apiUrl = localStorage.getItem("apiUrl");
  const dbTokenData = localStorage.getItem("dbTokenData");

  const headers = {
    Authorization: `Bearer ${dbTokenData}`,
    "Content-Type": "application/json",
    dbMaster: "",
  };

  const [loading, setLoading] = useState(false);
  const [oraganizationData, setOraganizationData] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [editShow, setEditShow] = useState(false);

  useEffect(() => {
    fetchData();
  }, [refresh]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const response = await axios.get(apiUrl + "organization", { headers });

      const data = Array.isArray(response.data) ? response.data : [];

      setOraganizationData(data);

      if (data.length > 0 && data[0]?.id) {
        setSelectedData([data[0].id]);
      } else {
        setSelectedData([]);
      }

      setRefresh(false);
    } catch (error) {
      setOraganizationData([]);
      setSelectedData([]);
      setRefresh(false);
    } finally {
      setLoading(false);
    }
  };

  const editHandleShow = () => {
    if (oraganizationData.length === 0) {
      toast.warning("Company profile not available.");
      return;
    }

    const profileId = selectedData[0] || oraganizationData[0]?.id;

    if (!profileId) {
      toast.warning("Company profile not available.");
      return;
    }

    setEditShow(true);
  };

  const editHandleClose = () => {
    setShowConfirm(true);
  };

  const handleConfirmClose = () => {
    setShowConfirm(false);
    setEditShow(false);
  };

  const handleCancelClose = () => {
    setShowConfirm(false);
  };

  const profile = oraganizationData.length > 0 ? oraganizationData[0] : null;

  const getInitials = (name) => {
    if (!name) return "OR";

    const parts = name.trim().split(/\s+/).filter(Boolean);

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const getLogo = () => {
    if (!profile?.imageString) {
      return null;
    }

    return `data:image/png;base64,${profile.imageString}`;
  };

  const getAddress = () => {
    if (!profile) {
      return "—";
    }

    return [
      profile.addressline1,
      profile.addressline2,
      profile.city,
      profile.dist,
      profile.state,
      profile.country,
      profile.pin,
    ]
      .filter(Boolean)
      .join(", ");
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          boxSizing: "border-box",
          px: {
            xs: 1,
            sm: 1.5,
            md: 2,
          },
          py: {
            xs: 1,
            sm: 1.5,
          },
          backgroundColor: BACKGROUND,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: '100%',
            mx: "auto",
            overflow: "hidden",
            border: `1px solid ${BORDER}`,
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 4px 18px rgba(18,59,93,0.07)",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 2,
                sm: 2.5,
                md: 3,
              },
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderBottom: `1px solid ${BORDER}`,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: {
                    xs: 18,
                    sm: 20,
                  },
                  fontWeight: 700,
                  color: NAVY,
                  lineHeight: 1.2,
                }}
              >
                Company Profile
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 12,
                  color: MUTED,
                }}
              >
                Organization information and contact details
              </Typography>
            </Box>

            <MasterButton
              variant="update"
              tooltip="Edit Company Profile"
              onClick={editHandleShow}
              disabled={loading || !profile}
            >
              Update Profile
            </MasterButton>
          </Box>

          {profile ? (
            <Box
              sx={{
                p: {
                  xs: 1.25,
                  sm: 1.75,
                  md: 2.25,
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  minHeight: {
                    xs: 180,
                    sm: 205,
                  },
                  borderRadius: "11px",
                  background:
                    "linear-gradient(135deg, #2F6FA5 0%, #123B5D 65%, #0D2F49 100%)",
                  display: "flex",
                  alignItems: "center",
                  px: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                  },
                  py: {
                    xs: 2,
                    sm: 2.5,
                  },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    width: 280,
                    height: 280,
                    borderRadius: "50%",
                    top: -160,
                    right: -80,
                    background: "rgba(255,255,255,0.06)",
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    width: 180,
                    height: 180,
                    borderRadius: "50%",
                    bottom: -120,
                    right: 110,
                    background: "rgba(255,255,255,0.04)",
                  }}
                />

                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: {
                      xs: 1.5,
                      sm: 2,
                      md: 2.5,
                    },
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: {
                        xs: 72,
                        sm: 88,
                      },
                      height: {
                        xs: 72,
                        sm: 88,
                      },
                      flexShrink: 0,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      backgroundColor: "#FFFFFF",
                      border: "4px solid rgba(255,255,255,0.88)",
                      boxShadow: "0 6px 18px rgba(0,0,0,0.20)",
                    }}
                  >
                    {getLogo() ? (
                      <Box
                        component="img"
                        src={getLogo()}
                        alt={profile.name || "Organization"}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: NAVY,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: {
                              xs: 22,
                              sm: 28,
                            },
                            fontWeight: 700,
                            color: "#FFFFFF",
                          }}
                        >
                          {getInitials(profile.name)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#FFFFFF",
                          fontSize: {
                            xs: 21,
                            sm: 25,
                            md: 28,
                          },
                          fontWeight: 700,
                          lineHeight: 1.15,
                        }}
                      >
                        {profile.name || "Organization"}
                      </Typography>

                      <Chip
                        label="Registered Organization"
                        size="small"
                        sx={{
                          height: 24,
                          borderRadius: "6px",
                          backgroundColor: "#DDF5E7",
                          color: "#167A43",
                          fontSize: 10.5,
                          fontWeight: 700,
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        mt: 0.8,
                        color: "rgba(255,255,255,0.78)",
                        fontSize: 12.5,
                      }}
                    >
                      Organization Code:{" "}
                      <Box
                        component="span"
                        sx={{
                          color: "#FFFFFF",
                          fontWeight: 700,
                        }}
                      >
                        {profile.code || "—"}
                      </Box>
                    </Typography>

                    {profile.type && (
                      <Typography
                        sx={{
                          mt: 0.35,
                          color: "rgba(255,255,255,0.68)",
                          fontSize: 11.5,
                        }}
                      >
                        Organization Type: {profile.type}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 1.5,
                  mt: 1.5,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: `1px solid ${BORDER}`,
                    borderRadius: "9px",
                    background:
                      "linear-gradient(180deg, #FFFFFF 0%, #FAFCFE 100%)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: MUTED,
                      mb: 0.45,
                    }}
                  >
                    Trust
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#243746",
                    }}
                  >
                    {profile.trust || "—"}
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: `1px solid ${BORDER}`,
                    borderRadius: "9px",
                    background:
                      "linear-gradient(180deg, #FFFFFF 0%, #FAFCFE 100%)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: MUTED,
                      mb: 0.45,
                    }}
                  >
                    Establishment Year
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#243746",
                    }}
                  >
                    {profile.establishmentyear || "—"}
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: `1px solid ${BORDER}`,
                    borderRadius: "9px",
                    background:
                      "linear-gradient(180deg, #FFFFFF 0%, #FAFCFE 100%)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: MUTED,
                      mb: 0.45,
                    }}
                  >
                    Organization Type
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#243746",
                    }}
                  >
                    {profile.type || "—"}
                  </Typography>
                </Paper>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1fr 1fr",
                  },
                  gap: 1.5,
                  mt: 1.5,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    border: `1px solid ${BORDER}`,
                    borderRadius: "10px",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "9px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#EAF2FA",
                        color: NAVY,
                      }}
                    >
                      <PhoneOutlinedIcon fontSize="small" />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          fontWeight: 700,
                          color: "#243746",
                        }}
                      >
                        Contact Information
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.2,
                          fontSize: 10.5,
                          color: MUTED,
                        }}
                      >
                        Primary contact details
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.35 }} />

                  <Box
                    sx={{
                      display: "grid",
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <PhoneOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#7890A3",
                        }}
                      />

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 10.5,
                            color: MUTED,
                          }}
                        >
                          Contact
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.15,
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: "#2D4152",
                          }}
                        >
                          {profile.contact || "—"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                      }}
                    >
                      <EmailOutlinedIcon
                        sx={{
                          mt: 0.1,
                          fontSize: 18,
                          color: "#7890A3",
                        }}
                      />

                      <Box
                        sx={{
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10.5,
                            color: MUTED,
                          }}
                        >
                          Email
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.15,
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: "#2D4152",
                            wordBreak: "break-word",
                          }}
                        >
                          {profile.emailid || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    border: `1px solid ${BORDER}`,
                    borderRadius: "10px",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "9px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#EAF2FA",
                        color: NAVY,
                      }}
                    >
                      <LocationOnOutlinedIcon fontSize="small" />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          fontWeight: 700,
                          color: "#243746",
                        }}
                      >
                        Location
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.2,
                          fontSize: 10.5,
                          color: MUTED,
                        }}
                      >
                        Registered office location
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.35 }} />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <LocationOnOutlinedIcon
                      sx={{
                        mt: 0.1,
                        fontSize: 19,
                        color: "#7890A3",
                      }}
                    />

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          color: MUTED,
                        }}
                      >
                        Address
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.2,
                          fontSize: 13,
                          lineHeight: 1.55,
                          fontWeight: 600,
                          color: "#2D4152",
                        }}
                      >
                        {getAddress()}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  mt: 1.5,
                  p: 1.75,
                  border: `1px solid ${BORDER}`,
                  borderRadius: "10px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "9px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#EAF2FA",
                      color: NAVY,
                    }}
                  >
                    <BusinessOutlinedIcon fontSize="small" />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: "#243746",
                      }}
                    >
                      Organization Information
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.2,
                        fontSize: 10.5,
                        color: MUTED,
                      }}
                    >
                      General company details
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 1.35 }} />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(3, 1fr)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      borderRight: {
                        xs: "none",
                        sm: `1px solid ${BORDER}`,
                      },
                      borderBottom: {
                        xs: `1px solid ${BORDER}`,
                        sm: "none",
                      },
                    }}
                  >
                    <HandshakeOutlinedIcon
                      sx={{
                        fontSize: 18,
                        color: "#7890A3",
                        mb: 0.3,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: MUTED,
                      }}
                    >
                      Trust
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.15,
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: "#2D4152",
                      }}
                    >
                      {profile.trust || "—"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      borderRight: {
                        xs: "none",
                        sm: `1px solid ${BORDER}`,
                      },
                      borderBottom: {
                        xs: `1px solid ${BORDER}`,
                        sm: "none",
                      },
                    }}
                  >
                    <CalendarMonthOutlinedIcon
                      sx={{
                        fontSize: 18,
                        color: "#7890A3",
                        mb: 0.3,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: MUTED,
                      }}
                    >
                      Establishment Year
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.15,
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: "#2D4152",
                      }}
                    >
                      {profile.establishmentyear || "—"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.75,
                    }}
                  >
                    <PublicOutlinedIcon
                      sx={{
                        fontSize: 18,
                        color: "#7890A3",
                        mb: 0.3,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: MUTED,
                      }}
                    >
                      Organization Type
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.15,
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: "#2D4152",
                      }}
                    >
                      {profile.type || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <MasterButton
                  variant="update"
                  tooltip="Edit Company Profile"
                  onClick={editHandleShow}
                  disabled={loading}
                >
                  Update Profile
                </MasterButton>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                py: 9,
                textAlign: "center",
              }}
            >
              <BusinessOutlinedIcon
                sx={{
                  fontSize: 50,
                  color: "#A8B6C2",
                }}
              />

              <Typography
                sx={{
                  mt: 1,
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#526273",
                }}
              >
                No company profile found
              </Typography>

              <Typography
                sx={{
                  mt: 0.4,
                  fontSize: 12,
                  color: MUTED,
                }}
              >
                Organization information is not available.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {loading && <CustomLoader />}

      <MasterDialog
        open={editShow}
        onHide={editHandleClose}
        title="Edit Company Profile"
        maxWidth="md"
      >
        <Oraganization
          selectedId={selectedData && selectedData[0]}
          mode="U"
          onHide={editHandleClose}
          setRefresh={setRefresh}
        />
      </MasterDialog>

      <MasterConfirmDialog
        show={showConfirm}
        title="Close Confirmation"
        message="Are you sure you want to close this modal?"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
};

export default OraganizationListing;
